import Router from "next/router";
import { api } from "./api";

const MAX_EVENTS_PER_SESSION = 40;
const LOBBY_SNAPSHOT_INTERVAL_MS = 60000;
const DIRECT_FALLBACK_HOST = "direct.chesshotel.com";
const DIRECT_FALLBACK_SOURCE_HOSTS = new Set([
  "www.chesshotel.com",
  "chesshotel.com",
]);
const DIRECT_FALLBACK_STORAGE_KEY = "chesshotel:ru-direct-fallback-url";

type ClientHealthPayload = Record<string, any>;

declare global {
  interface Window {
    __chesshotelRussiaClientHealth?: {
      enabled?: boolean;
      sessionId?: string;
      source?: string;
    };
  }
}

let enabled = false;
let source = "app";
let geo: string | null = null;
let ws: number | null = null;
let eventsSent = 0;
let listenersAttached = false;
let enabledReported = false;
let firstPongReported = false;
let lastLobbySnapshotAt = 0;
let lastLobbySnapshotKey = "";
const reportedResourceUrls = new Set<string>();

function isCdnNextStaticAsset(url: string) {
  return url.includes("cdn.chesshotel.com") && url.includes("/_next/static/");
}

function canUseDirectFallback() {
  if (typeof window === "undefined") return false;

  const host = window.location.hostname.toLowerCase();
  return DIRECT_FALLBACK_SOURCE_HOSTS.has(host);
}

function buildDirectFallbackUrl() {
  return `https://${DIRECT_FALLBACK_HOST}${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function redirectRussiaToDirect(reason: string, payload: ClientHealthPayload) {
  if (!canUseDirectFallback()) return;

  const targetUrl = buildDirectFallbackUrl();
  try {
    if (window.sessionStorage.getItem(DIRECT_FALLBACK_STORAGE_KEY) === targetUrl) {
      return;
    }
    window.sessionStorage.setItem(DIRECT_FALLBACK_STORAGE_KEY, targetUrl);
  } catch (_) {}

  reportRussiaClientHealth("direct_fallback_redirect", {
    reason,
    targetUrl,
    ...payload,
  });

  window.setTimeout(() => {
    window.location.replace(targetUrl);
  }, 150);
}

function getSessionId() {
  if (typeof window === "undefined") return "";

  const inlineSessionId = window.__chesshotelRussiaClientHealth?.sessionId;
  if (inlineSessionId) return inlineSessionId;

  try {
    let sessionId = window.sessionStorage.getItem(
      "chesshotel:client-health-session-id"
    );
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.sessionStorage.setItem(
        "chesshotel:client-health-session-id",
        sessionId
      );
    }
    return sessionId;
  } catch (_) {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function getConnectionInfo() {
  if (typeof navigator === "undefined") return null;

  const nav = navigator as any;
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection || {};

  return {
    effectiveType: connection.effectiveType || null,
    downlink: connection.downlink || null,
    rtt: connection.rtt || null,
    saveData: connection.saveData || false,
  };
}

function getNavigationTiming() {
  if (typeof performance === "undefined") return null;

  const navigation = performance.getEntriesByType?.("navigation")?.[0] as
    | PerformanceNavigationTiming
    | undefined;

  if (!navigation) return null;

  return {
    domContentLoadedMs: Math.round(navigation.domContentLoadedEventEnd),
    loadMs: Math.round(navigation.loadEventEnd),
    responseStartMs: Math.round(navigation.responseStart),
    responseEndMs: Math.round(navigation.responseEnd),
    transferSize: navigation.transferSize || null,
    encodedBodySize: navigation.encodedBodySize || null,
    decodedBodySize: navigation.decodedBodySize || null,
  };
}

function getEndpoint() {
  return `${api.defaults.baseURL || "/api/v1/"}client-health`;
}

function isEnabled() {
  return (
    enabled ||
    (typeof window !== "undefined" &&
      window.__chesshotelRussiaClientHealth?.enabled === true)
  );
}

function send(body: Record<string, any>) {
  if (typeof window === "undefined") return;
  if (eventsSent >= MAX_EVENTS_PER_SESSION) return;

  eventsSent++;

  const json = JSON.stringify(body);
  const endpoint = getEndpoint();

  if (navigator.sendBeacon) {
    const blob = new Blob([json], { type: "application/json" });
    if (navigator.sendBeacon(endpoint, blob)) return;
  }

  fetch(endpoint, {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
    },
    body: json,
  }).catch(() => {});
}

export function reportRussiaClientHealth(
  event: string,
  payload: ClientHealthPayload = {}
) {
  if (!isEnabled()) return;

  send({
    event,
    source,
    sessionId: getSessionId(),
    geo,
    ws,
    userId: payload.userId || null,
    gameId: payload.gameId || null,
    locale: Router.locale || "",
    href: window.location.href,
    path: window.location.pathname,
    referrer: document.referrer,
    timestamp: Date.now(),
    visibilityState: document.visibilityState,
    online: navigator.onLine,
    connection: getConnectionInfo(),
    navigation: getNavigationTiming(),
    payload,
  });
}

function reportResourceError(event: Event) {
  const target = event.target as HTMLScriptElement | HTMLLinkElement | null;
  const url =
    target && "src" in target && target.src
      ? target.src
      : target && "href" in target && target.href
      ? target.href
      : "";

  if (!url || reportedResourceUrls.has(url)) return;
  reportedResourceUrls.add(url);

  reportRussiaClientHealth("resource_error", {
    url,
    tagName: target?.tagName || null,
    rel: target && "rel" in target ? target.rel : null,
    nextStatic: url.includes("/_next/static/"),
    cdn: url.includes("cdn.chesshotel.com"),
  });

  if (isCdnNextStaticAsset(url)) {
    redirectRussiaToDirect("cdn_next_static_error", { url });
  }
}

function attachListeners() {
  if (listenersAttached || typeof window === "undefined") return;
  listenersAttached = true;

  window.addEventListener("error", (event) => {
    if (event.target && event.target !== window) {
      reportResourceError(event);
      return;
    }

    const errorEvent = event as ErrorEvent;
    reportRussiaClientHealth("window_error", {
      message: errorEvent.message,
      source: errorEvent.filename,
      line: errorEvent.lineno,
      column: errorEvent.colno,
    });
  }, true);

  window.addEventListener("unhandledrejection", (event) => {
    reportRussiaClientHealth("unhandled_rejection", {
      reason:
        typeof event.reason === "string"
          ? event.reason
          : event.reason?.message || String(event.reason),
    });
  });

  window.addEventListener("load", () => {
    reportRussiaClientHealth("window_load");
  });

  window.addEventListener("pagehide", () => {
    reportRussiaClientHealth("page_hide");
  });
}

export function enableRussiaClientHealth(input: {
  geo?: string | null;
  ws?: number | null;
  source?: string;
} = {}) {
  if (typeof window === "undefined") return false;

  const nextGeo = input.geo ? String(input.geo).toUpperCase() : geo;
  const inlineEnabled =
    window.__chesshotelRussiaClientHealth?.enabled === true;

  if (nextGeo !== "RU" && !inlineEnabled) {
    return false;
  }

  enabled = true;
  geo = nextGeo || geo;
  ws = input.ws ?? ws;
  source = input.source || window.__chesshotelRussiaClientHealth?.source || source;
  window.__chesshotelRussiaClientHealth = {
    ...(window.__chesshotelRussiaClientHealth || {}),
    enabled: true,
    sessionId: getSessionId(),
    source,
  };

  attachListeners();

  if (!enabledReported) {
    enabledReported = true;
    reportRussiaClientHealth("client_health_enabled", {
      geo,
      ws,
      source,
    });
  }

  return true;
}

export function reportRussiaSiteSettings(input: {
  geo?: string | null;
  ws?: number | null;
}) {
  if (
    enableRussiaClientHealth({
      geo: input.geo,
      ws: input.ws,
      source: "site-settings",
    })
  ) {
    reportRussiaClientHealth("site_settings_ok", input);
  }
}

export function reportRussiaSiteSettingsFailed(error: any) {
  reportRussiaClientHealth("site_settings_failed", {
    message: error?.message,
    status: error?.response?.status,
  });
}

export function reportRussiaWebsocketEvent(
  event: string,
  payload: ClientHealthPayload = {}
) {
  if (event === "websocket_pong") {
    if (firstPongReported) return;
    firstPongReported = true;
  }

  reportRussiaClientHealth(event, payload);
}

export function reportRussiaLobbySnapshot(
  reason: string,
  snapshot: ClientHealthPayload
) {
  if (!isEnabled()) return;

  const now = Date.now();
  const key = JSON.stringify({
    invites: snapshot.invites,
    tmpInvites: snapshot.tmpInvites,
    gamesCreated: snapshot.gamesCreated,
    ws,
  });

  if (
    key === lastLobbySnapshotKey &&
    now - lastLobbySnapshotAt < LOBBY_SNAPSHOT_INTERVAL_MS
  ) {
    return;
  }

  lastLobbySnapshotKey = key;
  lastLobbySnapshotAt = now;

  reportRussiaClientHealth("lobby_snapshot", {
    reason,
    ...snapshot,
  });
}
